"use client";

import { useState } from "react";
import Image from "next/image";
import { ContentBadge } from "@/components/site/content-badge";

export type MealItem = {
  id: number;
  name: string;
  description: string;
  image: string;
  nutrients: string[];
  /** 사진 표시 기준점 (Tailwind object-position 클래스) — 주요 음식이 틀 안에 오도록 */
  imagePosition?: string;
  /** 실사진 기반 AI 재구성 이미지 표시용 플래그. 안내 문구 노출은 대표 결정으로 중단(2026-09-25), 데이터는 보존 */
  aiReconstructed?: boolean;
};


type TabKey = "meals" | "snacks";

const tabs: { key: TabKey; label: string }[] = [
  { key: "meals", label: "특별식" },
  { key: "snacks", label: "간식" },
];

const nutrientTone = (nutrient: string) =>
  nutrient.includes("저당") ? "primary" : "neutral";

export function MealTabs({
  meals,
  snacks,
  otherSnacks = [],
}: {
  meals: MealItem[];
  snacks: MealItem[];
  /** 대표 카드 아래 작게 보여주는 그 밖의 실제 제공 간식 */
  otherSnacks?: { name: string; src: string }[];
}) {
  const [tab, setTab] = useState<TabKey>("meals");
  const isMeals = tab === "meals";

  return (
    <div className="flex flex-col">
      {/* 세그먼티드 탭 */}
      <div data-reveal className="mb-6 flex justify-center md:mb-10">
        <div
          role="tablist"
          aria-label="특별식 · 간식 선택"
          className="inline-flex w-full gap-1 rounded-xl bg-fill p-1 sm:w-auto"
        >
          {tabs.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.key)}
                className={`flex-1 rounded-lg px-6 py-2 text-[15px] font-semibold transition-colors sm:flex-none sm:px-10 ${
                  active
                    ? "bg-surface text-brand shadow-[0_1px_4px_rgba(112,115,124,0.1)]"
                    : "text-label-alt hover:text-label"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 헤딩 */}
      <div data-reveal className="mb-7 flex flex-col gap-1.5 text-center">
        <h2 className="font-display text-[22px] font-bold tracking-[-0.4px] md:text-[32px]">
          {isMeals ? "월 2~4회 제공되는 특별식" : "정성껏 준비한 간식"}
        </h2>
        <p className="text-[15px] text-label-alt md:text-base">
          {isMeals
            ? "파트너 요양원에 실제로 제공한 특별식입니다."
            : "죽과 요거트, 빵과 전 등 다양한 오후 간식을 준비합니다."}
        </p>
      </div>

      {isMeals ? (
        <div className="grid gap-3.5 md:grid-cols-2 md:gap-5">
          {meals.map((meal) => (
            <article
              key={meal.id}
              className="flex flex-col overflow-hidden rounded-[20px] border border-line bg-surface"
            >
              <div className="relative aspect-[4/3] w-full">
                <Image
                  src={meal.image}
                  alt={meal.name}
                  fill
                  className={`object-cover ${meal.imagePosition ?? "object-center"}`}
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              </div>
              <div className="flex flex-col gap-2.5 p-[18px] md:p-7">
                <h3 className="text-[17px] font-bold md:text-[18px]">
                  {meal.name}
                </h3>
                <p className="text-sm text-label-alt md:text-[15px]">
                  {meal.description}
                </p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {meal.nutrients.map((n) => (
                    <ContentBadge key={n} tone={nutrientTone(n)}>
                      {n}
                    </ContentBadge>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
          {snacks.map((snack) => (
            <article
              key={snack.id}
              className="flex flex-col overflow-hidden rounded-xl border border-line bg-surface md:rounded-2xl"
            >
              <div className="relative aspect-[4/3] w-full">
                <Image
                  src={snack.image}
                  alt={snack.name}
                  fill
                  className={`object-cover ${snack.imagePosition ?? "object-center"}`}
                  sizes="(max-width: 768px) 50vw, 33vw"
                />
              </div>
              <div className="flex flex-1 flex-col gap-1 p-3.5 md:gap-2 md:p-[22px]">
                <h3 className="text-[15px] font-bold md:text-[17px]">
                  {snack.name}
                </h3>
                <p className="text-xs text-label-alt md:text-sm">
                  {snack.description}
                </p>
                <div className="mt-auto hidden flex-wrap gap-1.5 pt-1.5 md:flex">
                  {snack.nutrients.map((n) => (
                    <ContentBadge key={n} tone={nutrientTone(n)}>
                      {n}
                    </ContentBadge>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
      {!isMeals && otherSnacks.length > 0 ? (
        <div className="mt-8 md:mt-10">
          <p className="mb-3 text-[13px] font-bold tracking-[0.04em] text-label-alt md:text-sm">그 밖의 실제 제공 간식</p>
          <div className="grid grid-cols-3 gap-2.5 md:gap-4">
            {otherSnacks.map((s) => (
              <figure key={s.src} className="flex flex-col gap-1.5">
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-surface-alt">
                  <Image src={s.src} alt={s.name} fill className="object-cover" sizes="(max-width: 768px) 33vw, 260px" />
                </div>
                <figcaption className="text-[12px] font-semibold text-label md:text-[14px]">{s.name}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
